// React hooks over the offline runtime: what the UI gates on.
//
//  - useOnlineGate: an online-only feature (new analysis, the builder, Deck
//    Chat, Ask the Judge, set check, Watchtower, URL imports, plain-English
//    search, collection edits) is disabled with "Needs a connection" while C1
//    is not online. Without the runtime C1 never leaves `online`.
//  - useEditGate: in C2 `rejected` every edit control is disabled with
//    "Sign in again to make changes" (read-only, data kept).
import { useSyncExternalStore } from 'react'
import { useConnectivity } from './connectivity'
import { useIdentity } from './identity'
import { CONN, MODE } from './offline.codes'
import { getPendingWork, offlineRecoveryBlocked, subscribePendingWork, subscribeRecoveryBlocked, type PendingWork } from './runtime'
import { useCommonT } from '../../i18n'

export function useOnlineGate(): { online: boolean; reason: string | null } {
  const t = useCommonT()
  const conn = useConnectivity()
  const online = conn === CONN.online
  return { online, reason: online ? null : t('offline.needs_connection') }
}

export function useEditGate(): { canEdit: boolean; reason: string | null } {
  const t = useCommonT()
  const id = useIdentity()
  const rejected = id.mode === MODE.rejected
  return { canEdit: !rejected, reason: rejected ? t('offline.rejected.reason') : null }
}

export function usePendingWork(): PendingWork {
  return useSyncExternalStore(subscribePendingWork, getPendingWork, getPendingWork)
}

/** A committed sign-out / switch is still being finished on this device (nothing of any owner shows). */
export function useRecoveryBlocked(): boolean {
  return useSyncExternalStore(subscribeRecoveryBlocked, offlineRecoveryBlocked, () => false)
}
